import React from 'react';
import { certifications } from '../mock';
import { ShieldCheck, Building2 } from 'lucide-react';
import { Reveal, StaggerGroup, StaggerItem } from './Reveal';

const Certifications = () => {
  return (
    <section id="certifications" className="relative py-24 lg:py-32 bg-[#fafaf7] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-16">
        <Reveal>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-[1px] bg-[#c4a24a]" />
            <span className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] uppercase">
              05 — Trust &amp; Compliance
            </span>
          </div>
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-16">
            <h2
              className="lg:col-span-8 text-[42px] lg:text-[60px] xl:text-[68px] leading-[1.03] font-medium text-[#0f2c6b] text-balance"
              style={{ fontFamily: 'Fraunces, serif', letterSpacing: '-0.03em' }}
            >
              Certified <span className="italic text-[#c4a24a] font-light">excellence.</span>
            </h2>
            <p className="lg:col-span-4 text-[14.5px] leading-[1.8] text-slate-600 font-light">
              Backed by leading national and international certifications for food safety, quality management, and manufacturing excellence.
            </p>
          </div>
        </Reveal>

        <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 max-w-3xl mx-auto" delay={0.08}>
          {certifications.map((cert, idx) => (
            <StaggerItem key={idx}>
              <CertificationBadge cert={cert} />
            </StaggerItem>
          ))}
        </StaggerGroup>

        {/* Marquee stripe */}
        <div className="mt-16 relative overflow-hidden py-8 border-y border-slate-200">
          <div className="flex animate-marquee whitespace-nowrap gap-16">
            {[...Array(2)].flatMap((_, i) => [
              <MarqueeItem key={`a-${i}`} text="FOOD SAFETY CERTIFIED" />,
              <MarqueeItem key={`b-${i}`} text="FSSAI COMPLIANT" />,
              <MarqueeItem key={`c-${i}`} text="MSME REGISTERED" />,
              <MarqueeItem key={`d-${i}`} text="REGISTERED DESIGN" />,
              <MarqueeItem key={`e-${i}`} text="BOTTLE TRADEMARKS MANAGED" />,
              <MarqueeItem key={`f-${i}`} text="BIS QUALITY STANDARDS" />,
              <MarqueeItem key={`g-${i}`} text="MADE IN INDIA" />,
            ])}
          </div>
        </div>
      </div>
    </section>
  );
};

const MarqueeItem = ({ text }) => (
  <div className="flex items-center gap-16 shrink-0">
    <span
      className="text-[24px] lg:text-[32px] font-medium text-[#0f2c6b]/80 tracking-wide"
      style={{ fontFamily: 'Fraunces, serif' }}
    >
      {text}
    </span>
    <span className="text-[#c4a24a] text-[20px]">✱</span>
  </div>
);

const CertificationBadge = ({ cert }) => {
  const renderBadge = () => {
    switch (cert.img) {
      case 'fssai':
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div
                className="text-[42px] font-normal italic text-[#7a2f2f]"
                style={{ fontFamily: 'Georgia, serif', letterSpacing: '-0.02em' }}
              >
                fssai
              </div>
              <div className="text-[8px] text-slate-500 tracking-widest mt-1 font-medium">
                FOOD SAFETY AND STANDARDS
              </div>
              <div className="text-[8px] text-slate-500 tracking-widest font-medium">
                AUTHORITY OF INDIA
              </div>
            </div>
          </div>
        );
      case 'iso22000':
      case 'iso9001':
        return (
          <div className="flex items-center justify-center h-full">
            <div className="relative w-28 h-28 rounded-full border-4 border-[#0f2c6b] flex flex-col items-center justify-center bg-white">
              <div className="text-[9px] font-semibold text-[#0f2c6b] tracking-[0.2em] absolute top-2.5">
                CERTIFIED
              </div>
              <div
                className="text-[22px] font-medium text-[#0f2c6b] leading-none"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                ISO
              </div>
              <div className="text-[10.5px] font-semibold text-[#0f2c6b] mt-1">
                {cert.img === 'iso22000' ? '22000:2018' : '9001:2015'}
              </div>
              <div className="text-[7px] text-slate-500 absolute bottom-2.5 tracking-widest">QMS</div>
            </div>
          </div>
        );
      case 'haccp':
        return (
          <div className="flex items-center justify-center h-full">
            <div className="relative w-28 h-28 rounded-full border-4 border-emerald-700 flex flex-col items-center justify-center bg-white">
              <ShieldCheck size={24} className="text-emerald-700 mb-1" strokeWidth={1.5} />
              <div
                className="text-[18px] font-medium text-emerald-800 leading-none"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                HACCP
              </div>
              <div className="text-[7px] text-slate-500 mt-1.5 tracking-widest">CERTIFIED</div>
            </div>
          </div>
        );
      case 'msme':
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center px-3">
              <div className="flex items-center justify-center gap-1">
                <Building2 size={26} className="text-[#0f2c6b]" strokeWidth={1.3} />
                <div
                  className="text-[30px] font-medium text-[#0f2c6b] italic tracking-tight"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  MSME
                </div>
              </div>
              <div className="text-[8px] text-slate-500 tracking-widest mt-2 font-medium">
                MINISTRY OF MICRO, SMALL
              </div>
              <div className="text-[8px] text-slate-500 tracking-widest font-medium">
                &amp; MEDIUM ENTERPRISES
              </div>
            </div>
          </div>
        );
      case 'bis':
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full border-4 border-[#0f2c6b] flex items-center justify-center relative">
                <div
                  className="text-[24px] font-medium text-[#0f2c6b] leading-none"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  B
                </div>
              </div>
              <div
                className="text-[13px] font-medium text-[#0f2c6b] mt-2"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                BIS
              </div>
              <div className="text-[8px] text-slate-500 tracking-widest">(IF APPLICABLE)</div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="group relative bg-white p-6 h-40 flex items-center justify-center border border-slate-100 hover:border-[#c4a24a]/40 hover:shadow-[0_10px_40px_-15px_rgba(15,44,107,0.2)] hover:-translate-y-1 transition-all duration-500 cursor-default">
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#c4a24a] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
      {renderBadge()}
    </div>
  );
};

export default Certifications;
